import React, { useState, useEffect } from 'react';
import { Card, Spinner, Badge } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { generateSlug } from '../utils/slugUtils';
import { useAuth } from '../context/AuthContext';

function SimilarRestaurants({ restaurantId }) {
  const [similar, setSimilar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { token } = useAuth();

  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

  useEffect(() => {
    fetchSimilarRestaurants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantId, token]);

  const fetchSimilarRestaurants = async () => {
    try {
      // #248: owner-aware endpoint — the reference restaurant may be the viewer's
      // own private add, which 404s without the token.
      const response = await fetch(
        `${apiBaseUrl}/restaurants/${restaurantId}/similar?limit=8`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch similar restaurants');
      }

      const data = await response.json();
      setSimilar(data.similar_restaurants || []);
    } catch (err) {
      console.error('Error fetching similar restaurants:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRestaurantClick = (restaurant) => {
    const slug = generateSlug(restaurant.name);
    navigate(`/restaurant/${restaurant.id}/${slug}`);
    // Scroll to top when navigating to new restaurant
    window.scrollTo(0, 0);
  };

  const getSimilarityBadgeVariant = (score) => {
    if (score >= 0.8) return 'success';  // 80-100%: green
    if (score >= 0.6) return 'info';     // 60-79%: blue
    if (score >= 0.4) return 'warning';  // 40-59%: yellow
    return 'secondary';                   // 20-39%: gray
  };

  if (loading) {
    return (
      <div className="text-center py-3">
        <Spinner animation="border" size="sm" /> Loading similar restaurants...
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center text-muted py-3">
        <small>Unable to load similar restaurants</small>
      </div>
    );
  }

  if (similar.length === 0) {
    return (
      <div className="text-center text-muted py-3">
        <small>No similar restaurants found</small>
      </div>
    );
  }

  return (
    <div>
      <div style={{ textAlign: 'center' }}>
        <div
          style={{
            display: 'inline-flex',
            overflowX: 'auto',
            gap: '12px',
            paddingBottom: '8px',
            scrollBehavior: 'smooth',
            WebkitOverflowScrolling: 'touch',
            maxWidth: '100%',
            textAlign: 'left'
          }}
        >
          {similar.map((restaurant) => (
            <div
              key={restaurant.id}
              style={{
                flex: '0 0 auto',
                width: '145px',
                cursor: 'pointer'
              }}
              onClick={() => handleRestaurantClick(restaurant)}
            >
              <Card className="h-100 shadow-sm hover-card">
                {restaurant.primary_image ? (
                  <Card.Img
                    variant="top"
                    src={restaurant.primary_image}
                    alt={restaurant.name}
                    style={{
                      height: '90px',
                      objectFit: 'cover'
                    }}
                  />
                ) : (
                  <div
                    style={{
                      height: '90px',
                      backgroundColor: '#f0f0f0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <span style={{ fontSize: '1.5rem' }}>🍽️</span>
                  </div>
                )}
                <Card.Body className="p-2">
                  <Card.Title
                    style={{
                      fontSize: '0.8rem',
                      fontWeight: '600',
                      marginBottom: '0.25rem',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {restaurant.name}
                  </Card.Title>

                  <div className="mb-1" style={{ fontSize: '0.7rem' }}>
                    <Badge
                      bg={getSimilarityBadgeVariant(restaurant.similarity_score)}
                      style={{ fontSize: '0.65rem' }}
                    >
                      {Math.round(restaurant.similarity_score * 100)}% match
                    </Badge>
                  </div>

                  {restaurant.shared_categories && restaurant.shared_categories.length > 0 && (
                    <div
                      className="mb-1"
                      style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontSize: '0.6rem'
                      }}
                    >
                      {restaurant.shared_categories.slice(0, 2).map((cat, idx) => (
                        <Badge
                          key={idx}
                          bg="light"
                          text="dark"
                          className="me-1"
                          style={{ fontSize: '0.55rem' }}
                        >
                          {cat}
                        </Badge>
                      ))}
                    </div>
                  )}

                  {restaurant.cuisines && restaurant.cuisines.length > 0 && (
                    <div
                      className="small text-muted"
                      style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontSize: '0.65rem'
                      }}
                    >
                      {restaurant.cuisines.join(' • ')}
                    </div>
                  )}

                  {!!restaurant.is_closed && (
                    <Badge bg="danger" style={{ fontSize: '0.55rem', marginTop: '0.25rem' }}>
                      CLOSED
                    </Badge>
                  )}
                  {restaurant.total_locations > 1 && (
                    <Badge
                      bg="info"
                      style={{
                        fontSize: '0.55rem',
                        marginTop: '0.25rem',
                        marginLeft: restaurant.is_closed ? '0.25rem' : '0'
                      }}
                    >
                      {restaurant.total_locations} locations
                    </Badge>
                  )}
                </Card.Body>
              </Card>
            </div>
          ))}
        </div>
      </div>

      {similar.length >= 6 && (
        <div className="text-center mt-2">
          <small className="text-muted" style={{ fontSize: '0.75rem' }}>
            ← Scroll to see all {similar.length} similar restaurants →
          </small>
        </div>
      )}
    </div>
  );
}

export default SimilarRestaurants;
